import { useRecoilCallback } from 'recoil'
import type {
  EditorTextBlockInput,
  EditorTextBoxInput,
} from '@messenger/lotus/row-store'
import type { ApplicationType } from '@messenger/types'
import {
  getEditorPageContent,
  saveEditorPageContent,
} from '@/api/editorPageContent'
import {
  editorPageContentAtomFamily,
  editorTextBoxDocumentsSelector,
  editorTextBoxDraftsAtom,
} from '@/atoms/editorTextBoxAtoms'
import {
  canvasPageIdAtom,
  selectedItemsAtom,
  unsavedChangesAtom,
} from '@/atoms/_editorAtoms'
import { applicationTypeAtom } from '@/atoms/globalAtoms'
import { useEditorSaveGesture } from '@/hooks/editor/useEditorSaveGesture'
import { useSyncCurrentPageToEditorPages } from '@/hooks/editor/useSyncCurrentPageToEditorPages'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { editorTextBoxDocumentToInput } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { logger, EventCategory } from '@/utils/logger'

export const useEditorApplyTextBoxTemplate = () => {
  const syncCurrentPageToEditorPages = useSyncCurrentPageToEditorPages()
  const runSaveGesture = useEditorSaveGesture()
  const handleError = useErrorHandler('useEditorApplyTextBoxTemplate')

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (
        boxId: string,
        blocks: EditorTextBlockInput[],
      ): Promise<boolean> => {
        const pageId = await snapshot.getPromise(canvasPageIdAtom)
        if (!pageId) return false
        // The atom is declared in a JavaScript module, so its value arrives
        // untyped.
        const applicationType = (await snapshot.getPromise(
          applicationTypeAtom,
        )) as ApplicationType
        // The write below writes the line, so the swap is a Save of the line
        // and runs as one: under the tab's save latch, after a version of the
        // line. A swap while another Save is writing does not run at all.
        const applied = await runSaveGesture(
          'swap_text_box_template',
          applicationType,
          async () => {
            const [results, drafts] = await Promise.all([
              snapshot.getPromise(editorTextBoxDocumentsSelector(pageId)),
              snapshot.getPromise(editorTextBoxDraftsAtom),
            ])
            const source =
              drafts[boxId] ?? results?.get(boxId)?.document ?? null
            if (source === null) {
              handleError(
                'The template could not be applied to this text box',
                new Error(`no document for box ${boxId}`),
              )
              return false
            }
            const box: EditorTextBoxInput = {
              ...editorTextBoxDocumentToInput(source),
              blocks,
            }
            try {
              await saveEditorPageContent(pageId, {
                boxes: [box],
                deleteBoxIds: [],
              })
              const { data: bundle } = await getEditorPageContent(pageId)
              await syncCurrentPageToEditorPages()
              set(editorPageContentAtomFamily(pageId), bundle)
              set(editorTextBoxDraftsAtom, (prev) => {
                if (!(boxId in prev)) return prev
                const { [boxId]: _replaced, ...rest } = prev
                void _replaced
                return rest
              })
              set(selectedItemsAtom, [boxId])
              set(unsavedChangesAtom, true)
            } catch (error) {
              handleError(
                'The template could not be applied to this text box',
                error instanceof Error ? error : new Error(String(error)),
              )
              return false
            }
            logger.info({
              message: 'editor_apply_text_box_template_success',
              category: EventCategory.INSERT,
              data: { pageId, boxId },
            })
            return true
          },
        )
        // A swap that never ran, because another Save holds the line, is not
        // a swap that applied.
        return applied === true
      },
    [runSaveGesture, syncCurrentPageToEditorPages, handleError],
  )
}

export default useEditorApplyTextBoxTemplate
